"use client";
import React, { useEffect, useState } from "react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import {
  Combobox as HeadlessCombobox,
  ComboboxInput as HeadlessComboboxInput,
  ComboboxButton as HeadlessComboboxButton,
  ComboboxOptions as HeadlessComboboxOptions,
  ComboboxOption as HeadlessComboboxOption,
} from "@headlessui/react";
import clsx from "clsx";
import { RefCallBack } from "react-hook-form";
import { Avatar } from "../atoms";

export type Option = {
  id: string;
  name: string;
  disabled?: true;
  image?: string;
};

interface ComboboxProps {
  name: string;
  options: Option[];
  inputName?: string;
  inputRef?: RefCallBack;
  defaultValue?: Option;
  handleChange: (option: Option) => void;
  handleCreate: (query: string) => void;
  inputStyles?: string;
  optionsStyles?: string;
  hidden?: boolean;
}

export function Combobox({
  name,
  options,
  inputName,
  inputRef,
  defaultValue,
  handleChange,
  handleCreate,
  inputStyles,
  optionsStyles,
  hidden = false,
}: ComboboxProps) {
  const [query, setQuery] = useState("");
  const [selectedOption, setSelectedOption] = useState<Option>();

  const filteredOption =
    query === ""
      ? options
      : options.filter((option) => {
          return option.name.toLowerCase().includes(query.toLowerCase());
        });

  useEffect(() => {
    if (selectedOption) {
      if (selectedOption.id === "create") {
        handleCreate(query);
        setQuery("");
      } else {
        handleChange({ id: selectedOption.id, name: selectedOption.name });
      }
    }
  }, [selectedOption]);

  return (
    <HeadlessCombobox
      as="div"
      name={inputName}
      ref={inputRef}
      defaultValue={defaultValue ? defaultValue : selectedOption}
      onChange={setSelectedOption}
      immediate>
      {({ activeOption }) => (
        <div className="relative mt-1">
          <HeadlessComboboxInput
            placeholder={name ? `Search for ${name}` : "Search"}
            className={`w-full rounded-md border border-zinc-950/20 hover:border-zinc-950/30 bg-white py-2.5 pl-3 pr-12 placeholder:text-zinc-400 text-gray-900 focus:ring-2 focus:ring-inset focus:ring-blue-500 appearance-none focus: outline-none sm:text-sm sm:leading-6 ${inputStyles} `}
            displayValue={(option: Option) => option?.name}
            onChange={(event) => setQuery(event.target.value)}
          />
          <HeadlessComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
            <ChevronUpDownIcon
              className="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </HeadlessComboboxButton>
          <HeadlessComboboxOptions className="absolute z-[10000] mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1.5 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            {filteredOption.map((option: Option) => (
              <HeadlessComboboxOption
                key={option.id}
                value={option}
                disabled={option.disabled}
                className={() =>
                  clsx(
                    `relative cursor-default select-none py-3 pl-3 pr-9 rounded-md mx-1 ${optionsStyles}`,
                    activeOption && activeOption === option
                      ? "bg-blue-500 text-white"
                      : "text-gray-900",
                  )
                }>
                {({ selected }) => (
                  <>
                    <div className="flex justify-start items-center space-x-3">
                      {option?.image && (
                        <Avatar src={option.image} square className="size-6" />
                      )}
                      <span className={clsx("truncate", "font-medium")}>
                        {option.name}
                      </span>
                    </div>
                    {selected && (
                      <span
                        className={clsx(
                          "absolute inset-y-0 right-0 flex items-center pr-4",
                          activeOption && activeOption === option
                            ? "text-white"
                            : "text-indigo-600",
                        )}>
                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                      </span>
                    )}
                  </>
                )}
              </HeadlessComboboxOption>
            ))}
            {!hidden && (
              <HeadlessComboboxOption
                value={{ id: "create", name: query }}
                disabled={
                  !query ||
                  Boolean(
                    filteredOption.find(
                      (option) =>
                        option.name.toLowerCase() === query.toLowerCase(),
                    ),
                  )
                }
                className={({ active, disabled }) =>
                  clsx(
                    "relative cursor-default select-none py-2 pl-3 pr-9 mx-1 rounded-md",
                    active ? "bg-blue-500 text-white" : "text-gray-900",
                    disabled ? "cursor-not-allowed !text-gray-400" : "",
                  )
                }>
                Create {query ? query : `${name}`}
              </HeadlessComboboxOption>
            )}
          </HeadlessComboboxOptions>
        </div>
      )}
    </HeadlessCombobox>
  );
}
